/**
 * Botão "voltar" do celular (Android, gesto de voltar) na folha de camadas.
 *
 * A folha tem profundidade 0 (fechada), 1 (grade de camadas) ou 2 (ajustes
 * de uma camada). Cada nível aberto empilha uma entrada no histórico
 * (`pushState`, mesma URL); o voltar desempilha um nível — ajustes → grade →
 * fecha — e nunca tira a pessoa do app enquanto a folha está aberta.
 *
 * Quando a própria interface fecha níveis (✕, "voltar às camadas"), as
 * entradas empilhadas são consumidas com `history.go(-n)`, e o `popstate`
 * resultante é ignorado. A URL do estado (`urlSync`) é gravada com
 * `replaceState` na entrada atual; depois de um voltar ela é regravada, para
 * não ficar a da entrada antiga.
 */
import { useEffect, type RefObject } from 'react';
import { writeUrlNow } from '../state/urlSync';
import { useUiStore, type SheetStage } from '../state/ui';
import { escapeLayerAbove } from './modalStack';

/** Identifica as entradas DESTA carga da página (após recarregar, as antigas valem 0). */
const SESSION = Math.random().toString(36).slice(2);

interface SheetEntry {
  amaSheet: { session: string; depth: number };
}

export function sheetDepth(open: boolean, stage: SheetStage): number {
  if (!open) return 0;
  return stage === 'camadas' ? 1 : 2;
}

function depthOf(state: unknown): number {
  const e = (state as Partial<SheetEntry> | null)?.amaSheet;
  return e && e.session === SESSION && typeof e.depth === 'number' ? e.depth : 0;
}

/**
 * Liga a folha ao histórico enquanto montado (a folha só existe no celular).
 * `layer` é o elemento da folha na pilha de camadas (`modalStack`): com um
 * diálogo aberto por cima dela, o voltar fecha só o diálogo.
 */
export function useSheetHistory(layer: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    // Entradas empilhadas por nós acima da entrada-base da página.
    let pushed = depthOf(window.history.state);
    let ignorePops = 0;
    // `history.go` é assíncrono: enquanto a travessia não chega, empilhar
    // agora poria a entrada no lugar errado. A sincronização espera o popstate.
    let pendente = false;

    const onPop = (e: PopStateEvent) => {
      const depth = depthOf(e.state);
      if (ignorePops > 0) {
        ignorePops--;
        pushed = depth;
        if (ignorePops === 0 && pendente) {
          pendente = false;
          const ui = useUiStore.getState();
          sync(ui.sheetOpen, ui.sheetStage);
        }
        writeUrlNow();
        return;
      }
      const ui = useUiStore.getState();
      const current = sheetDepth(ui.sheetOpen, ui.sheetStage);
      if (depth < current && escapeLayerAbove(layer.current)) {
        // Voltar fechou o diálogo de cima; a folha fica onde estava, e a
        // entrada consumida é reposta.
        const entry: SheetEntry = { amaSheet: { session: SESSION, depth: current } };
        window.history.pushState({ ...(window.history.state ?? {}), ...entry }, '');
        pushed = current;
        return;
      }
      pushed = depth;
      if (depth < current) {
        if (depth === 0) ui.set({ sheetOpen: false, sheetStage: 'camadas' });
        else ui.set({ sheetStage: 'camadas' });
      }
      writeUrlNow();
    };

    function sync(open: boolean, stage: SheetStage): void {
      if (ignorePops > 0) {
        pendente = true;
        return;
      }
      const depth = sheetDepth(open, stage);
      if (depth > pushed) {
        for (let d = pushed + 1; d <= depth; d++) {
          const entry: SheetEntry = { amaSheet: { session: SESSION, depth: d } };
          window.history.pushState({ ...(window.history.state ?? {}), ...entry }, '');
        }
        pushed = depth;
      } else if (depth < pushed) {
        const n = pushed - depth;
        pushed = depth;
        ignorePops++;
        window.history.go(-n);
      }
    }

    window.addEventListener('popstate', onPop);
    // Remontagem (ex.: a tela girou para desktop e voltou) com entradas nossas
    // acima do estado atual: desempilha para realinhar.
    const initial = useUiStore.getState();
    sync(initial.sheetOpen, initial.sheetStage);
    const unsub = useUiStore.subscribe((s, prev) => {
      if (s.sheetOpen !== prev.sheetOpen || s.sheetStage !== prev.sheetStage) sync(s.sheetOpen, s.sheetStage);
    });
    return () => {
      window.removeEventListener('popstate', onPop);
      unsub();
      // A folha desmonta com o layout (a tela virou desktop): fecha e consome
      // as entradas, para o voltar do desktop não ter toques sem efeito.
      if (pushed > 0) {
        window.addEventListener('popstate', () => writeUrlNow(), { once: true });
        window.history.go(-pushed);
        pushed = 0;
      }
      if (useUiStore.getState().sheetOpen) useUiStore.getState().set({ sheetOpen: false, sheetStage: 'camadas' });
    };
  }, [layer]);
}
